import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Mimic Mote (Act III, ACT3-BIBLE.md §8) — an Understudy echo.
 *
 * It fires a weak copy of the player's current weapon, in the player's own
 * weapon colour. Not the weapon's real code (that is the Understudy's whole
 * point, and a boss-sized thing to do); just its colour and its cadence, close
 * enough that a wave of them reads unmistakably as "that's mine". The lesson it
 * teaches ahead of the boss is a small, cheap one: your build is information the
 * act has, and it is going to use it.
 *
 * `mirrorColor` and `fireInterval` are handed in by MinionWaveSystem from the
 * player's live loadout, so a run that swaps weapons swaps what these look like.
 */
export class MimicMote {
  constructor(eventBus, {
    x, y, radius = 3, hp, moveSpeed, contactDamage,
    shotDamage, shotSpeed, fireInterval, mirrorColor = '#7fe0a0', preferredRange = 110,
  }) {
    this.eventBus = eventBus;
    this.type = 'mimicMote';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.shotDamage = shotDamage;
    this.shotSpeed = shotSpeed;
    this.fireInterval = fireInterval;
    // Read by Game._drawMinions and used for its bullets — the tell that makes
    // the whole creature land.
    this.mirrorColor = mirrorColor;
    this.preferredRange = preferredRange;
    this.isDead = false;
    this._fireTimer = fireInterval * Math.random();
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player, bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    // Holds the player's own preferred fighting distance, which is the joke:
    // it plays your build the way you'd play it.
    const approach = dist > this.preferredRange ? 1 : -0.6;
    this.x += (dx / dist) * this.moveSpeed * approach * dt;
    this.y += (dy / dist) * this.moveSpeed * approach * dt;
    this._clampToArena();

    this._fireTimer -= dt;
    if (this._fireTimer <= 0 && bulletPool) {
      this._fireTimer = this.fireInterval;
      bulletPool.fire({
        x: this.x,
        y: this.y,
        vx: (dx / dist) * this.shotSpeed,
        vy: (dy / dist) * this.shotSpeed,
        radius: 2.5,
        damage: this.shotDamage,
        color: this.mirrorColor,
        lifetime: 3,
      });
    }

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
